/* eslint-disable react-hooks/exhaustive-deps */
import { produce } from 'immer';
import _ from 'lodash';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ClickOutside from '../../../../../common/widgets/ClickOutside';
import Icons from '../../../../../common/widgets/Icons';
import { config } from '../../../config';
import { CitySuggestion } from '../../../models/Discover';
import { resources } from '../../../resources/resources';
import { filterService } from '../../../services/filters';
import PromoSearchSuggestions from './PromoSearchSuggestions';

type SearchState = {
  sugestions: CitySuggestion[] | null;
  searchValue: string;
  areSuggestionActive: boolean;
};
type LoadingState = { areSugestionsLoading: boolean; isLoading: boolean };

const PromoSearch = () => {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const autocompleteRef = useRef<HTMLDivElement | null>(null);

  const [searchState, setSearchState] = useState<SearchState>({
    sugestions: null,
    searchValue: '',
    areSuggestionActive: false,
  });
  const [loadingState, setLoadingState] = useState<LoadingState>({
    areSugestionsLoading: false,
    isLoading: false,
  });

  const debounceSearchChange = _.debounce(
    async (abortController: AbortController) => {
      setLoadingState(
        produce(d => {
          d.areSugestionsLoading = true;
        }),
      );

      const result = await filterService.getLocationAutocompleteSuggestions(
        searchState.searchValue,
        {
          signal: abortController?.signal,
        },
      );

      if (!result.hasErrors) {
        setSearchState(
          produce(d => {
            d.sugestions = result.data;
          }),
        );
      }

      setLoadingState(
        produce(d => {
          d.areSugestionsLoading = false;
        }),
      );
    },
    config.debounceDelay,
  );

  //#region event handlers
  const handleSearchChange = (e: any) => {
    if (!searchState.areSuggestionActive) {
      setSearchState(
        produce(d => {
          d.areSuggestionActive = true;
        }),
      );
    }

    if (!searchState.sugestions) {
      setSearchState(
        produce(d => {
          d.sugestions = [];
        }),
      );
    }

    if (
      !(
        (e.target.value as string).startsWith(searchState.searchValue) ||
        searchState.searchValue.startsWith(e.target.value)
      )
    ) {
      setSearchState(
        produce(d => {
          d.sugestions = null;
        }),
      );
    }

    setSearchState(
      produce(d => {
        d.searchValue = e.target.value as string;
      }),
    );
  };

  const handleSearch = (searchTerm: string) => {
    if (searchTerm.length < 2) return;

    navigate(`/discover/benefitsIn/${searchTerm}`);
  };

  const handleKeyDownSearch = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearch(event.currentTarget.value);
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      autocompleteRef.current?.focus();
    }
  };

  const handleClickSearch = () => {
    handleSearch(searchState.searchValue);
  };

  const handleSuggestionClick = (suggestion: CitySuggestion) => {
    const formatedSuggestion = _.unescape(suggestion.cityName);
    setSearchState(
      produce(d => {
        d.searchValue = formatedSuggestion;
        d.areSuggestionActive = false;
      }),
    );

    navigate(`/discover/benefitsIn/${formatedSuggestion}`);
  };
  //#endregion

  //#region effects
  useEffect(() => {
    if (!searchState.searchValue)
      setSearchState(
        produce(d => {
          d.sugestions = null;
        }),
      );
  }, [searchState.searchValue]);

  useEffect(() => {
    if (searchState.searchValue.length > 2) {
      const abortController = new AbortController();
      debounceSearchChange(abortController);

      return () => {
        if (searchState.sugestions && searchState.sugestions?.length > 0)
          abortController.abort();
      };
    }
  }, [searchState.searchValue]);
  //#endregion

  //#region tsx
  return (
    <div
      className={`discover-bar-search sugestions-container ${
        !!searchState.sugestions && searchState.areSuggestionActive
          ? 'active-sugestions'
          : ''
      }`}
    >
      <ClickOutside
        className="search-outside-click"
        onClickOutside={() =>
          setSearchState(
            produce(d => {
              d.areSuggestionActive = false;
            }),
          )
        }
      >
        <>
          <span className="search-label">
            {translate(resources.discover.search.label)}
          </span>
          <input
            type="text"
            value={searchState.searchValue}
            placeholder={translate(resources.discover.search.placeholder)}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDownSearch}
            onFocus={() =>
              setSearchState(
                produce(d => {
                  d.areSuggestionActive = true;
                }),
              )
            }
          />
          <button
            className="btn btn-solid btn-discover"
            onClick={handleClickSearch}
          >
            <span className="btn-text">
              {translate(resources.discover.search.action)}
            </span>
            <span className="btn-icon">
              <Icons.SearchMobile />
            </span>
          </button>
          {searchState.areSuggestionActive && (
            <PromoSearchSuggestions
              suggestions={searchState.sugestions}
              searchValue={searchState.searchValue}
              isLoading={loadingState.areSugestionsLoading}
              suggestionsRef={autocompleteRef}
              onClick={handleSuggestionClick}
            />
          )}
        </>
      </ClickOutside>
    </div>
  );
};

export default PromoSearch;
